import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { formaterDate, formaterIdentite } from '@gsm/partage';
import { useRessource, messageErreur } from '../api/ressource';
import { api, ErreurApi } from '../api/client';
import { useAuth } from '../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { BarreFiltres, FiltreRecherche } from '../composants/Filtres';
import { Champ } from '../composants/Champ';
import { Pastille } from '../composants/Pastille';
import { Modale } from '../composants/Modale';
import { Icone } from '../composants/Icone';
import { LienFiche } from '../composants/LienAction';
import { ModaleMandat, type ClientDuMandat } from '../composants/ModaleMandat';

interface Client {
  id: string;
  reference: string;
  nom: string;
  prenom: string;
  cin: string;
  dateNaissance: string;
  ville: string | null;
  mandats: { id: string; reference: string; statut: string }[];
}

interface ClientDetail {
  civilite: string | null;
  nom: string;
  prenom: string;
  cin: string;
  dateNaissance: string;
  nationalite: string | null;
  adresse: string | null;
  ville: string | null;
  telephone: string | null;
  email: string | null;
}

const VIDE = {
  civilite: '',
  nom: '',
  prenom: '',
  cin: '',
  dateNaissance: '',
  nationalite: '',
  adresse: '',
  ville: '',
  telephone: '',
  email: '',
};

/** Plafond de la réponse de `GET /clients` : au-delà, la recherche s'impose. */
const PLAFOND_CLIENTS = 200;

export function Clients() {
  const { aRole } = useAuth();
  const [recherche, setRecherche] = useState('');
  const { donnees, chargement, erreur, recharger } = useRessource<Client[]>(
    `/clients${recherche ? `?recherche=${encodeURIComponent(recherche)}` : ''}`,
  );
  const clients = donnees ?? [];
  const peutGerer = aRole('MIDDLE_BO', 'RESP_GESTION', 'ADMIN');
  const naviguer = useNavigate();
  const [clientDuMandat, setClientDuMandat] = useState<ClientDuMandat | null>(null);

  const [edition, setEdition] = useState<string | null>(null);
  const [saisie, setSaisie] = useState(VIDE);
  const [erreurForm, setErreurForm] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const enCreation = edition === '';

  const champ = (cle: keyof typeof VIDE) => ({
    value: saisie[cle],
    onChange: (e: { target: { value: string } }) => setSaisie({ ...saisie, [cle]: e.target.value }),
  });

  function ouvrirCreation() {
    setSaisie(VIDE);
    setEdition('');
    setErreurForm(null);
  }
  async function ouvrirEdition(c: Client) {
    setErreurForm(null);
    setEdition(c.id);
    // Pré-remplissage complet : la liste ne porte pas les coordonnées, on
    // recharge la fiche pour ne rien afficher de tronqué.
    try {
      const d = await api.get<ClientDetail>(`/clients/${c.id}`);
      setSaisie({
        civilite: d.civilite ?? '',
        nom: d.nom,
        prenom: d.prenom,
        cin: d.cin,
        dateNaissance: d.dateNaissance ? d.dateNaissance.slice(0, 10) : '',
        nationalite: d.nationalite ?? '',
        adresse: d.adresse ?? '',
        ville: d.ville ?? '',
        telephone: d.telephone ?? '',
        email: d.email ?? '',
      });
    } catch (e) {
      setErreurForm(
        e instanceof ErreurApi ? messageErreur(e) : 'Chargement de la fiche impossible.',
      );
    }
  }
  function fermer() {
    setEdition(null);
  }

  async function enregistrer(evenement: FormEvent) {
    evenement.preventDefault();
    setErreurForm(null);
    setOccupe(true);
    try {
      const corps = {
        ...saisie,
        civilite: saisie.civilite || undefined,
        nationalite: saisie.nationalite || undefined,
        adresse: saisie.adresse || undefined,
        ville: saisie.ville || undefined,
        telephone: saisie.telephone || undefined,
        email: saisie.email || undefined,
      };
      if (enCreation) {
        await api.post('/clients', corps);
      } else {
        await api.patch(`/clients/${edition}`, corps);
      }
      fermer();
      await recharger();
    } catch (e) {
      setErreurForm(e instanceof ErreurApi ? messageErreur(e) : 'Enregistrement impossible.');
    } finally {
      setOccupe(false);
    }
  }

  const colonnes: Colonne<Client>[] = [
    {
      cle: 'reference',
      entete: 'Référence',
      num: true,
      rendu: (c) => <LienFiche to={`/clients/${c.id}`}>{c.reference}</LienFiche>,
    },
    { cle: 'identite', entete: 'Client', rendu: (c) => formaterIdentite(c.nom, c.prenom) },
    { cle: 'cin', entete: 'CIN', num: true, rendu: (c) => c.cin },
    {
      cle: 'naissance',
      entete: 'Naissance',
      num: true,
      rendu: (c) => formaterDate(c.dateNaissance),
    },
    { cle: 'ville', entete: 'Ville', rendu: (c) => c.ville ?? '—' },
    {
      cle: 'mandat',
      entete: 'Mandat',
      rendu: (c) => {
        const vivant = c.mandats.find((m) => !['CLOTURE', 'ANNULE'].includes(m.statut));
        if (vivant) return <LienFiche to={`/mandats/${vivant.id}`}>{vivant.reference}</LienFiche>;
        if (!peutGerer) return <Pastille ton="attente">Sans mandat</Pastille>;
        return (
          <button
            type="button"
            className="bouton bouton--fantome bouton--icone bouton--compact"
            onClick={() => setClientDuMandat({ id: c.id, reference: c.reference })}
            aria-label={`Enregistrer un mandat pour ${formaterIdentite(c.nom, c.prenom)}`}
          >
            <Icone nom="creer" taille={14} />
            Enregistrer un mandat
          </button>
        );
      },
    },
    ...(peutGerer
      ? [
          {
            cle: 'actions',
            entete: '',
            num: true,
            rendu: (c: Client) => (
              <button
                type="button"
                className="tableau__action"
                onClick={() => void ouvrirEdition(c)}
                aria-label={`Éditer ${formaterIdentite(c.nom, c.prenom)}`}
              >
                <Icone nom="editer" taille={16} />
              </button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <p className="entete__fil">Référentiels</p>
      <div className="entete">
        <h1>Clients</h1>
        <span className="entete__ref">{clients.length} fiches</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}

      <Bloc
        titre="Fiches"
        meta={
          chargement
            ? 'chargement…'
            : clients.length >= PLAFOND_CLIENTS
              ? `${PLAFOND_CLIENTS} premiers par ordre alphabétique — affinez la recherche`
              : `${clients.length} fiche(s)`
        }
        actions={
          peutGerer && (
            <button
              type="button"
              className="bouton bouton--icone bouton--compact"
              onClick={ouvrirCreation}
            >
              <Icone nom="creer" taille={15} />
              Nouveau client
            </button>
          )
        }
      >
        <BarreFiltres actifs={recherche !== ''} onRetirer={() => setRecherche('')}>
          <FiltreRecherche
            valeur={recherche}
            onChange={setRecherche}
            placeholder="Nom, prénom, CIN ou référence"
          />
        </BarreFiltres>
        <Tableau colonnes={colonnes} lignes={clients} cle={(c) => c.id} vide="Aucun client." />
      </Bloc>

      <Modale
        ouverte={edition !== null}
        onFermer={fermer}
        surtitre="Client"
        titre={enCreation ? 'Nouveau client' : 'Modifier le client'}
        taille="large"
      >
        {erreurForm && (
          <BandeauAlerte nature="refus" titre="Enregistrement refusé">
            {erreurForm}
          </BandeauAlerte>
        )}
        <form onSubmit={(e) => void enregistrer(e)} noValidate>
          <div className="champs-en-ligne">
            <Champ label="Civilité" {...champ('civilite')} placeholder="M. / Mme" />
            <Champ label="Nom" {...champ('nom')} required />
            <Champ label="Prénom" {...champ('prenom')} required />
          </div>
          <div className="champs-en-ligne">
            <Champ
              label="CIN"
              {...champ('cin')}
              required
              aide="Unicité contrôlée."
              disabled={!enCreation}
            />
            <Champ label="Date de naissance" type="date" {...champ('dateNaissance')} required />
            <Champ label="Nationalité" {...champ('nationalite')} placeholder="Marocaine" />
          </div>
          <Champ label="Adresse" {...champ('adresse')} />
          <div className="champs-en-ligne">
            <Champ label="Ville" {...champ('ville')} />
            <Champ label="Téléphone" {...champ('telephone')} />
          </div>
          <Champ label="Adresse électronique" type="email" {...champ('email')} />
          <div className="modale__actions">
            <button type="button" className="bouton bouton--fantome" onClick={fermer}>
              Annuler
            </button>
            <button
              className="bouton"
              type="submit"
              disabled={
                occupe || !saisie.nom || !saisie.prenom || !saisie.cin || !saisie.dateNaissance
              }
            >
              {occupe ? 'Enregistrement…' : enCreation ? 'Créer le client' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </Modale>

      <ModaleMandat
        ouverte={clientDuMandat !== null}
        onFermer={() => setClientDuMandat(null)}
        client={clientDuMandat ?? undefined}
        // La suite du dossier (mandat signé, soumission) se fait sur la fiche
        // du mandat : on y conduit directement.
        onCree={(m) => naviguer(`/mandats/${m.id}`)}
      />
    </>
  );
}
